import { createContext, useContext, useMemo, useState } from 'react';
import { BACKEND_BASE_URL } from '../config/backendConfig';

// Backend-driven auth: login() calls the o2c-backend internal-user login
// endpoint and stores the returned JWT. Nothing here is a security boundary
// on its own — the backend is the source of truth for every credential and
// permission check; this just carries the token and surfaces display fields
// (name/role) so the dashboards can default their KAM filter to "just my
// clients" the same way the old mock auth did.
export const AUTH_STORAGE_KEY = 'arpflow.auth.token';
const AuthContext = createContext(null);

// Two KAM name forms because the existing dashboards key KAM identity
// differently: Campaigns + EMS use first name only; Onboarding uses the
// full name (see kam_name in the onboarding funnel SQL).
function toPublicUser(token, payload) {
  if (!payload) return null;
  const isKam = payload.role === 'KAM';
  return {
    token,
    email: payload.email,
    name: payload.name,
    role: payload.role,
    kamFirstName: isKam ? payload.name.split(' ')[0] : null,
    kamFullName: isKam ? payload.name : null,
  };
}

// JWT payload is the base64url-encoded middle segment. Decoded client-side
// only to read display fields — the backend has already verified the
// signature; this app never re-validates or trusts it for authorization.
function decodePayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function loadStoredUser() {
  const token = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!token) return null;
  const payload = decodePayload(token);
  if (!payload) {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
  return toPublicUser(token, payload);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(loadStoredUser);

  const value = useMemo(() => ({
    currentUser,
    login: async (email, password) => {
      if (!BACKEND_BASE_URL) return false;
      let response;
      try {
        response = await fetch(`${BACKEND_BASE_URL}/api/internalUsers/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
      } catch {
        return false;
      }
      if (!response.ok) return false;

      const { token } = await response.json();
      const payload = token && decodePayload(token);
      if (!payload) return false;

      localStorage.setItem(AUTH_STORAGE_KEY, token);
      setCurrentUser(toPublicUser(token, payload));
      return true;
    },
    logout: () => {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      setCurrentUser(null);
    },
  }), [currentUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider');
  return value;
}
